import { useState } from 'react';
import { X, Loader2, Key, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';

interface AccessCodeModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AccessCodeModal({ onClose, onSuccess }: AccessCodeModalProps) {
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify the access code exists and is valid
      const { data: invitation, error: invitationError } = await supabase
        .from('invitation_codes')
        .select('*')
        .eq('code', accessCode.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (invitationError) {
        throw invitationError;
      }

      if (!invitation) {
        setError('Invalid or expired access code. Please check your code and try again.');
        setLoading(false);
        return;
      }

      // Check if code has expired
      if (invitation.expires_at && new Date(invitation.expires_at) < new Date()) {
        setError('This access code has expired. Please request a new one.');
        setLoading(false);
        return;
      }

      // Check if code has reached max uses
      if (invitation.current_uses >= invitation.max_uses) {
        setError('This access code has reached its maximum number of uses.');
        setLoading(false);
        return;
      }

      // Store the valid access code in session storage for the signup/login process
      sessionStorage.setItem('valid_access_code', accessCode.trim());
      sessionStorage.setItem('access_code_email', invitation.email || '');

      toast.success('Access code verified! You can now sign in or create an account.');
      onSuccess();
      onClose();
    } catch (error) {
      logger.error('Error verifying access code', { error, codeLength: accessCode.length });
      setError('Failed to verify access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal">
      <div className="modal-content max-w-md bg-gray-800 border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">Enter Access Code</h3>
            <p className="text-sm text-gray-400">Enter your invitation code to continue</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="alert alert-info bg-accent-50 dark:bg-accent-900/20 border-accent-200 dark:border-accent-800">
            <Key className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-accent-300">
              Access codes are provided to waitlist members. If you don't have one yet, join the waitlist to receive yours.
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          <div>
            <label className="label text-gray-300">
              Access Code *
            </label>
            <input
              type="text"
              required
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter your access code"
              className="input bg-gray-900 border-gray-700 text-white placeholder-gray-500 uppercase"
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          <div className="pt-4 space-y-3">
            <button
              type="submit"
              disabled={loading || !accessCode.trim()}
              className="btn btn-primary w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Continue'
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline w-full text-gray-300 border-gray-600 hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Don't have an access code?{' '}
            <button
              type="button"
              onClick={() => {
                onClose();
                // This will trigger the waitlist modal
                setTimeout(() => {
                  const waitlistButton = document.querySelector('[data-waitlist-trigger]') as HTMLButtonElement;
                  waitlistButton?.click();
                }, 100);
              }}
              className="text-accent-400 hover:text-accent-300 underline"
            >
              Join the waitlist
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
